<template>
  <div class="ai-observability-grid">
    <div class="ai-summary-card">
      <span class="ai-summary-card__label">{{ $t("ncrm.ai.runs.summary.title") }}</span>
      <div class="ai-summary-list">
        <div class="ai-summary-list__item">
          <span class="ai-summary-list__label">{{ $t("ncrm.ai.runs.summary.totalAttempts") }}</span>
          <strong class="ai-summary-list__value">{{ formatIntegerLabel(observability.summary?.total_attempts) }}</strong>
        </div>
        <div class="ai-summary-list__item">
          <span class="ai-summary-list__label">{{ $t("ncrm.ai.runs.summary.succeededAttempts") }}</span>
          <strong class="ai-summary-list__value">{{ formatIntegerLabel(observability.summary?.succeeded_attempts) }}</strong>
        </div>
        <div class="ai-summary-list__item">
          <span class="ai-summary-list__label">{{ $t("ncrm.ai.runs.summary.failedAttempts") }}</span>
          <strong class="ai-summary-list__value">{{ formatIntegerLabel(observability.summary?.failed_attempts) }}</strong>
        </div>
        <div class="ai-summary-list__item">
          <span class="ai-summary-list__label">{{ $t("ncrm.ai.runs.summary.averageDuration") }}</span>
          <strong class="ai-summary-list__value">{{ formatDurationLabel(observability.summary?.average_duration_ms) }}</strong>
        </div>
      </div>
    </div>

    <div class="ai-summary-card">
      <span class="ai-summary-card__label">{{ $t("ncrm.ai.runs.usage.title") }}</span>
      <div class="ai-summary-list">
        <div class="ai-summary-list__item">
          <span class="ai-summary-list__label">{{ $t("ncrm.ai.runs.usage.inputTokens") }}</span>
          <strong class="ai-summary-list__value">{{ formatIntegerLabel(observability.usage?.input_tokens) }}</strong>
        </div>
        <div class="ai-summary-list__item">
          <span class="ai-summary-list__label">{{ $t("ncrm.ai.runs.usage.outputTokens") }}</span>
          <strong class="ai-summary-list__value">{{ formatIntegerLabel(observability.usage?.output_tokens) }}</strong>
        </div>
        <div class="ai-summary-list__item">
          <span class="ai-summary-list__label">{{ $t("ncrm.ai.runs.usage.totalTokens") }}</span>
          <strong class="ai-summary-list__value">{{ formatIntegerLabel(observability.usage?.total_tokens) }}</strong>
        </div>
        <div class="ai-summary-list__item">
          <span class="ai-summary-list__label">{{ $t("ncrm.ai.runs.usage.cachedTokens") }}</span>
          <strong class="ai-summary-list__value">{{ formatIntegerLabel(observability.usage?.cached_input_tokens) }}</strong>
        </div>
        <div class="ai-summary-list__item">
          <span class="ai-summary-list__label">{{ $t("ncrm.ai.runs.usage.webSearchCalls") }}</span>
          <strong class="ai-summary-list__value">{{ formatIntegerLabel(observability.usage?.web_search_calls) }}</strong>
        </div>
      </div>
    </div>

    <div class="ai-summary-card">
      <span class="ai-summary-card__label">{{ $t("ncrm.ai.runs.cost.title") }}</span>
      <div class="ai-summary-list">
        <div class="ai-summary-list__item">
          <span class="ai-summary-list__label">{{ $t("ncrm.ai.runs.cost.estimatedTotal") }}</span>
          <strong class="ai-summary-list__value">{{ formatUsdLabel(observability.cost?.estimated_total_usd) }}</strong>
        </div>
        <div class="ai-summary-list__item">
          <span class="ai-summary-list__label">{{ $t("ncrm.ai.runs.cost.attemptsEstimated") }}</span>
          <strong class="ai-summary-list__value">{{ formatIntegerLabel(observability.cost?.attempts_with_estimate) }}</strong>
        </div>
        <div class="ai-summary-list__item">
          <span class="ai-summary-list__label">{{ $t("ncrm.ai.runs.cost.pricingVersion") }}</span>
          <strong class="ai-summary-list__value">{{ observability.cost?.line_items?.[0]?.cost_estimate?.pricing_version || "—" }}</strong>
        </div>
      </div>
      <div v-if="!observability.cost?.estimate_available" class="ai-muted">
        {{ $t("ncrm.ai.runs.cost.unavailable") }}
      </div>
    </div>

    <div class="ai-summary-card">
      <span class="ai-summary-card__label">{{ $t("ncrm.ai.runs.policy.title") }}</span>
      <div class="ai-summary-list">
        <div class="ai-summary-list__item">
          <span class="ai-summary-list__label">{{ $t("ncrm.ai.runs.policy.maxAttempts") }}</span>
          <strong class="ai-summary-list__value">{{ formatIntegerLabel(observability.policy?.retry?.max_attempts) }}</strong>
        </div>
        <div class="ai-summary-list__item">
          <span class="ai-summary-list__label">{{ $t("ncrm.ai.runs.policy.backoff") }}</span>
          <strong class="ai-summary-list__value">{{ formatDurationLabel(observability.policy?.retry?.backoff_ms) }}</strong>
        </div>
        <div class="ai-summary-list__item">
          <span class="ai-summary-list__label">{{ $t("ncrm.ai.runs.policy.providerTimeout") }}</span>
          <strong class="ai-summary-list__value">{{ formatDurationLabel(observability.policy?.timeout?.provider_timeout_ms) }}</strong>
        </div>
        <div class="ai-summary-list__item">
          <span class="ai-summary-list__label">{{ $t("ncrm.ai.runs.policy.fallback") }}</span>
          <strong class="ai-summary-list__value">{{ $t("ncrm.ai.runs.policy.none") }}</strong>
        </div>
      </div>
    </div>

    <div v-if="observability.last_failure" class="ai-summary-card ai-summary-card--error ai-observability-grid__full">
      <span class="ai-summary-card__label">{{ $t("ncrm.ai.runs.lastFailure.title") }}</span>
      <div class="ai-summary-list">
        <div class="ai-summary-list__item">
          <span class="ai-summary-list__label">{{ $t("ncrm.ai.runs.lastFailure.category") }}</span>
          <strong class="ai-summary-list__value">{{ observability.last_failure?.taxonomy?.label || "—" }}</strong>
        </div>
        <div class="ai-summary-list__item">
          <span class="ai-summary-list__label">{{ $t("ncrm.ai.runs.lastFailure.retryable") }}</span>
          <strong class="ai-summary-list__value">
            {{
              observability.last_failure?.taxonomy?.retryable
                ? $t("ncrm.ai.runs.lastFailure.yes")
                : $t("ncrm.ai.runs.lastFailure.no")
            }}
          </strong>
        </div>
        <div class="ai-summary-list__item">
          <span class="ai-summary-list__label">{{ $t("ncrm.ai.runs.lastFailure.action") }}</span>
          <strong class="ai-summary-list__value">{{ observability.last_failure?.taxonomy?.operator_action || "—" }}</strong>
        </div>
      </div>
      <pre class="ai-summary-card__value">{{ observability.last_failure?.message || "—" }}</pre>
    </div>
  </div>
</template>

<script>
import { formatDurationLabel, formatIntegerLabel, formatUsdLabel } from "./aiRunObservability.mjs";

export default {
  name: "AiRunObservabilityCard",
  props: {
    observability: {
      type: Object,
      required: true,
    },
  },
  methods: {
    formatDurationLabel,
    formatIntegerLabel,
    formatUsdLabel,
  },
};
</script>
